
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Restaurant {
  id: string;
  name: string;
  description: string;
  image_url: string;
  rating: number;
  address: string;
  created_at: string;
  is_open: boolean;
  opening_hours: any;
  average_rating?: number;
}

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  restaurant_id: string;
  restaurant?: {
    name: string;
    address: string;
    is_open: boolean;
  };
}

interface SearchResults {
  restaurants: Restaurant[];
  menuItems: MenuItem[];
  loading: boolean;
}

export const useSearch = (query: string): SearchResults => {
  const [results, setResults] = useState<SearchResults>({
    restaurants: [],
    menuItems: [],
    loading: false,
  });

  useEffect(() => {
    const searchData = async () => {
      if (!query || query.length < 2) {
        setResults({ restaurants: [], menuItems: [], loading: false });
        return;
      }

      setResults(prev => ({ ...prev, loading: true }));

      try {
        // Search restaurants only (not marketplace)
        const { data: restaurantData, error: restaurantError } = await supabase
          .from('restaurants')
          .select('id, name, description, image_url, rating, address, created_at, is_open, opening_hours')
          .eq('business_type', 'restaurant')
          .or(`name.ilike.%${query}%,description.ilike.%${query}%,address.ilike.%${query}%`)
          .limit(10);

        // Search menu items with restaurant info
        const { data: menuItemData, error: menuItemError } = await supabase
          .from('menu_items')
          .select(`
            id, name, description, price, restaurant_id,
            restaurants!inner(name, address, is_open)
          `)
          .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
          .eq('is_available', true)
          .limit(10);

        if (restaurantError) throw restaurantError;
        if (menuItemError) throw menuItemError;

        // Process restaurant data with defaults
        const processedRestaurants = restaurantData?.map(restaurant => ({
          ...restaurant,
          created_at: restaurant.created_at || new Date().toISOString(),
          is_open: restaurant.is_open ?? true,
          opening_hours: restaurant.opening_hours || {}
        })) || [];

        // Process menu items data
        const processedMenuItems = menuItemData?.map(item => ({
          ...item,
          restaurant: Array.isArray(item.restaurants) ? item.restaurants[0] : item.restaurants
        })) || [];

        setResults({
          restaurants: processedRestaurants,
          menuItems: processedMenuItems,
          loading: false,
        });
      } catch (error) {
        console.error('Search error:', error);
        setResults({ restaurants: [], menuItems: [], loading: false });
      }
    };

    const debounceTimer = setTimeout(searchData, 300);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  return results;
};
